import { useEffect, useRef, useState } from "react";
import { API, getToken } from "@/lib/api";
import { X, Send, ExternalLink, BookOpen, Bot, Mic, Volume2, VolumeX } from "lucide-react";

export default function ScientistChat({ world, startKey = "nova", context = {}, onClose }) {
  const scientists = world.scientists || [];
  const [key, setKey] = useState(startKey);
  const sci = scientists.find((s) => s.key === key) || scientists[0] || { name: "Dr. Nova", accent: "#38e1ff" };
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sources, setSources] = useState([]);
  const [listening, setListening] = useState(false);
  const [voiceOut, setVoiceOut] = useState(false);
  const recog = useRef(null);
  const sessionId = useRef(Math.random().toString(36).slice(2));
  const endRef = useRef();

  useEffect(() => {
    setMsgs([{ role: "sci", text: sci.greeting || `I'm ${sci.name}, an AI research scientist. Ask me anything about the research.` }]);
    setSources([]);
  }, [key]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const speak = (t) => {
    try { const u = new SpeechSynthesisUtterance(t.replace(/\*\*/g, "").replace(/\*/g, "")); u.rate = 1.03; u.pitch = 1; window.speechSynthesis.cancel(); window.speechSynthesis.speak(u); } catch {}
  };

  const startMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input isn't supported in this browser. Please type instead."); return; }
    if (listening) { recog.current?.stop(); setListening(false); return; }
    const r = new SR(); r.lang = "en-US"; r.interimResults = false; r.maxAlternatives = 1;
    setListening(true);
    r.onresult = (e) => { const t = e.results[0][0].transcript; setListening(false); send(t); };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recog.current = r; r.start();
  };

  const send = async (override) => {
    const q = (override ?? text).trim();
    if (!q || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "sci", text: "" }]);
    let full = "";
    try {
      const res = await fetch(`${API}/vr/scientist/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
        body: JSON.stringify({ scientist_key: key, session_id: sessionId.current, text: q, context }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) { full += j.delta; setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "sci", text: c[c.length - 1].text + j.delta }; return c; }); }
            if (j.sources) setSources(j.sources);
            if (j.done && voiceOut && full) speak(full);
            if (j.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "sci", text: "Research service is unavailable right now." }; return c; });
          } catch {}
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "sci", text: "Connection error. Please try again." }; return c; });
    } finally { setBusy(false); }
  };

  const accent = sci.accent || "#38e1ff";
  return (
    <div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center p-0 md:p-4" data-testid="vr-scientist-chat">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:max-w-lg h-[85vh] md:h-[640px] rounded-t-3xl md:rounded-3xl bg-[#080c12] border border-white/10 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center gap-3" style={{ background: `linear-gradient(90deg, ${accent}18, transparent)` }}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${accent}22`, border: `1px solid ${accent}` }}>
            <Bot size={18} style={{ color: accent }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-white leading-tight">{sci.name}</div>
            <div className="text-[10px] font-mono tracking-widest" style={{ color: accent }}>AI RESEARCH SCIENTIST</div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="px-3 py-2 flex gap-2 overflow-x-auto border-b border-white/5">
          {scientists.map((s) => (
            <button key={s.key} onClick={() => setKey(s.key)} data-testid={`vr-sci-${s.key}`}
              className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-mono border transition ${key === s.key ? "text-black" : "text-slate-300 border-white/15"}`}
              style={key === s.key ? { background: s.accent, borderColor: s.accent } : {}}>
              {s.name}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-cyan-400 text-black" : "bg-white/[0.06] text-slate-100 border border-white/10"}`}>
                {(m.text || (busy && i === msgs.length - 1 ? "…" : "")).replace(/\*\*/g, "").replace(/\*/g, "")}
              </div>
            </div>
          ))}
          {sources.length > 0 && (
            <div className="rounded-2xl bg-black/40 border border-white/10 p-3">
              <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400 mb-2"><BookOpen size={12} /> VIEW SOURCES</div>
              <div className="space-y-1.5">
                {sources.map((s, i) => (
                  <a key={i} href={s.url || "#"} target="_blank" rel="noreferrer"
                    className="block text-xs text-cyan-300 hover:text-cyan-200 flex items-start gap-1.5">
                    <ExternalLink size={11} className="mt-0.5 shrink-0" />
                    <span>{s.title}{s.year ? ` (${s.year})` : ""} — <span className="text-slate-500">{s.peptide}</span></span>
                  </a>
                ))}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-white/10 flex gap-2 items-center">
          <button onClick={startMic} data-testid="vr-sci-mic" title="Speak"
            className={`w-10 h-10 shrink-0 rounded-xl border flex items-center justify-center ${listening ? "bg-rose-500 text-white border-rose-500 animate-pulse" : "border-white/15 text-slate-300 hover:text-cyan-300"}`}><Mic size={16} /></button>
          <button onClick={() => { setVoiceOut((v) => !v); if (voiceOut) window.speechSynthesis?.cancel(); }} data-testid="vr-sci-voice-toggle" title="Spoken replies"
            className={`w-10 h-10 shrink-0 rounded-xl border flex items-center justify-center ${voiceOut ? "bg-cyan-400 text-black border-cyan-400" : "border-white/15 text-slate-300"}`}>{voiceOut ? <Volume2 size={16} /> : <VolumeX size={16} />}</button>
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask or tap the mic to speak…" data-testid="vr-sci-input"
            className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/60" />
          <button onClick={() => send()} disabled={busy} data-testid="vr-sci-send"
            className="px-4 h-10 rounded-xl bg-cyan-400 text-black disabled:opacity-50"><Send size={16} /></button>
        </div>
        <div className="px-3 pb-2 text-[10px] text-slate-500 font-mono text-center">AI research scientist · not a human · not medical advice</div>
      </div>
    </div>
  );
}
